import React from 'react';
import { NumberFormatProps, NumberFormatValues } from 'react-number-format';
import { extractFormCellProps, FormCell, IFormCellProps } from '../FormCell';
import { NumberInputStyled } from '../NumberInput';
import styled from 'styled-components';
import { mergeTests } from '../../test-utilities/mergeTests';

export interface IPhoneNumberInputProps extends IFormCellProps {
  /** HTML 'name' attribute */
  name?: string;
  /** The value attribute of the underlying input */
  value?: string | null;
  /** Disabled the input and prevents editing the contents */
  disabled?: boolean;
  /** This callback exposes the input reference / DOM node to parent components. */
  getInputRef?: (elem: HTMLInputElement) => void;
  /** A callback triggered when component is focused. */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** A callback triggered when focus is removed. */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** A callback fired when the value prop changes. */
  onChange?: (value: string | null) => void;
  /** Switches the input to read only which prevents editing the contents. */
  readOnly?: boolean;
}

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const normalizePhoneNumber = (value: string) => {
  if (value.length <= 10) {
    let normalizedValue = value;
    for (let i = 0; i < 10 - value.length; i++) {
      normalizedValue += '#';
    }
    return `(${normalizedValue.substr(0, 3)}) ${normalizedValue.substr(3, 3)}-${normalizedValue.substr(6, 4)}`;
  } else {
    return '+' + value.substr(0, 15);
  }
};

/**
 * A phone number input that will constrain inputs to valid US phone numbers
 * It takes a string in the form of ########## and will display the (###) ###-#### formatting.
 * The Value returned and the value you put in should not contain formatting since that is a display concern.
 */
export const PhoneNumberInput = (props: IPhoneNumberInputProps) => {
  const { formCellProps, inputProps } = extractFormCellProps(props);
  const onValueChange = (value: NumberFormatValues) => {
    if (inputProps.onChange) {
      inputProps.onChange(value.value ? value.value : null);
    }
  };
  const numberFormatProps: NumberFormatProps = {
    allowNegative: false,
    decimalScale: 0,
    disabled: inputProps.disabled,
    displayType: 'input',
    fixedDecimalScale: true,
    format: normalizePhoneNumber,
    getInputRef: inputProps.getInputRef,
    isNumericString: true,
    mask: '#',
    name: inputProps.name,
    onBlur: inputProps.onBlur,
    onFocus: inputProps.onFocus,
    onValueChange,
    placeholder: '(###) ###-####',
    readOnly: inputProps.readOnly,
    thousandSeparator: '',
    value: inputProps.value ? inputProps.value : '',
  };

  const dataTestClass = props['data-test-class'] || 'phone-input';
  return (
    <FormCell {...formCellProps}>
      <StyledWrapper>
        <NumberInputStyled
          {...numberFormatProps}
          showErrorHighlight={!!formCellProps.errorText}
          data-test-id={mergeTests(props['data-test-id'], 'input')}
          data-test-class={dataTestClass}
        />
      </StyledWrapper>
    </FormCell>
  );
};
